import { ProviderRegistration, ProviderRegistrationSearchResult } from "@asd/customer-portal-api";
import { formatDate, parseTimestamp } from "@asd/customer-portal-ui";
import { GridColDef, GridFilterModel, GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment-timezone";

import { ProviderRegistrationForDisplay } from "~/utils/providerRegistrationHelpers";

const MASTER_FIELDS: { key: string; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "services", label: "Services" },
  { key: "distance", label: "Distance (Miles)" },
  { key: "languages", label: "Language(s)" },
  { key: "genderDisclosure", label: "Gender" },
  { key: "genderPreference", label: "Employer Gender Preference" },
  { key: "address", label: "Address" },
  { key: "phoneNumber", label: "Phone Number" },
  { key: "workEmailAddress", label: "Email" },
  { key: "updatedDate", label: "Last Updated" },
];

export const DETAIL_FIELDS: { key: string; label: string }[] = [
  { key: "generalAvailability", label: "Availability" },
  { key: "maxHoursWeek", label: "Max hours per week" },
  { key: "professionalCredentials", label: "Credentials" },
  { key: "commuteIndependently", label: "Commute independently" },
  { key: "caregiverExperience", label: "Previous experience" },
  { key: "workWithMinors", label: "Works with minors" },
  { key: "considerations", label: "Considerations" },
];

const formatValue = (val: unknown): string => {
  if (val === true) return "Yes";
  if (val === false) return "No";
  return val != null ? String(val) : "";
};

const transformRowForCsv = (row: ProviderRegistrationForDisplay): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const { key, label } of MASTER_FIELDS) {
    const val = key === "distance" ? row.distance : row.registration[key as keyof ProviderRegistration];

    if (key === "services") {
      result[label] = (val as { key: string; value: string }[])?.map(({ value }) => value).join(", ") ?? "";
    } else if (key === "languages") {
      result[label] = (val as { code: string; name: string }[])?.map(({ name }) => name).join(", ") ?? "";
    } else if (key === "genderDisclosure") {
      result[label] = (val as { key: string; value: string }[])?.map(({ value }) => value).join(", ") ?? "";
    } else if (key === "genderPreference") {
      result[label] = (val as { key: string; value: string })?.value ?? "";
    } else if (key === "updatedDate" && val) {
      result[label] = formatDate(parseTimestamp(val as string));
    } else {
      result[label] = formatValue(val);
    }
  }

  for (const { key, label } of DETAIL_FIELDS) {
    const val = row.registration[key as keyof ProviderRegistration];

    if (key === "generalAvailability") {
      result[label] =
        Array.isArray(val) && val.length ? (val as unknown as string[]).map((d) => moment(d).format("ddd MM/DD h:mm a")).join(" | ") : "";
    } else if (key === "professionalCredentials") {
      result[label] = (val as { key: string; value: string }[])?.map(({ value }) => value).join(", ") ?? "";
    } else if (key === "considerations") {
      result[label] = (val as { key: string; value: string }[])?.map(({ value }) => value).join(", ") ?? "";
    } else if (val != null && typeof val === "object") {
      result[label] = Object.keys(val).join(", ");
    } else {
      result[label] = formatValue(val);
    }
  }

  return result;
};

const registrationValueGetter =
  (field: string) =>
  (_value: any, row: ProviderRegistrationSearchResult | { isDetail: boolean; master: ProviderRegistrationSearchResult }) => {
    const registration =
      "isDetail" in row && row.isDetail ? row.master.registration : (row as ProviderRegistrationSearchResult).registration;
    return (registration as Record<string, any>)[field];
  };

const renderArray = (key: string) => (params: GridRenderCellParams) => {
  return params.value?.map((item: any) => (typeof item === "object" ? item[key] : item)).join(", ");
};

const renderByKey = (key: string) => (params: GridRenderCellParams) => {
  return params.value?.[key];
};

export const extractSortValue = (field: string, val: any): string | number => {
  if (val === null || val === undefined) return "";
  if (typeof val === "number") return val;
  if (typeof val === "string") return val;
  if (field === "languages") return (val as { name: string }[]).map((l) => l.name).join(", ");
  if (field === "genderPreference") return (val as { value: string }).value ?? "";
  if (Array.isArray(val)) return val.map((item) => item.value ?? item.key ?? "").join(", ");
  return String(val);
};

export const BASE_COLUMNS: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 1, valueGetter: registrationValueGetter("name") },
  {
    field: "services",
    headerName: "Services",
    flex: 2,
    valueGetter: registrationValueGetter("services"),
    renderCell: renderArray("value"),
  },
  {
    field: "distance",
    headerName: "Distance (Miles)",
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams) => (params.value === 0 ? "< 1" : params.value),
  },
  {
    field: "languages",
    headerName: "Language(s)",
    flex: 2,
    valueGetter: registrationValueGetter("languages"),
    renderCell: renderArray("name"),
  },
  {
    field: "genderDisclosure",
    headerName: "Gender",
    flex: 1,
    valueGetter: registrationValueGetter("genderDisclosure"),
    renderCell: renderArray("value"),
  },
  {
    field: "genderPreference",
    headerName: "Employer Gender Preference",
    flex: 1,
    valueGetter: registrationValueGetter("genderPreference"),
    renderCell: renderByKey("value"),
  },
];

const EXCLUDED_SEARCH_FIELDS = new Set([
  "latitude",
  "longitude",
  "address",
  "phoneNumber",
  "providerRegistrationId",
  "updatedDate",
  "workEmailAddress",
]);

export const flattenForSearch = (provider: ProviderRegistrationForDisplay): string => {
  const reg = provider.registration;
  const values: string[] = [];

  if (provider.distance !== undefined) values.push(String(provider.distance));
  if (provider.normalizedAddress) values.push(provider.normalizedAddress);

  for (const [key, val] of Object.entries(reg)) {
    if (EXCLUDED_SEARCH_FIELDS.has(key)) continue;
    if (val === null || val === undefined) continue;

    if (typeof val === "boolean") {
      if (val) values.push(key);
    } else if (typeof val === "string" || typeof val === "number") {
      values.push(String(val));
    } else if (Array.isArray(val)) {
      for (const item of val) {
        if (typeof item === "string") {
          values.push(item);
        } else if (item instanceof Date) {
          if (!isNaN(item.getTime())) {
            values.push(item.toLocaleDateString("en-US", { weekday: "long" }));
          }
        } else if (typeof item === "object" && item !== null) {
          if ("value" in item && item.value) values.push(item.value);
          if ("name" in item && item.name) values.push(item.name);
        }
      }
    } else if (typeof val === "object") {
      if ("value" in val) values.push((val as { value: string }).value);
    }
  }

  return values.join(" ").toLowerCase();
};

export const applyFilterItem = (row: ProviderRegistrationForDisplay, filterItem: GridFilterModel["items"][number]): boolean => {
  if (!filterItem.field || !filterItem.operator) return true;
  if (filterItem.value === undefined || filterItem.value === "" || (Array.isArray(filterItem.value) && filterItem.value.length === 0)) {
    return true;
  }

  const rawCellValue =
    filterItem.field in row ? row[filterItem.field as keyof typeof row] : (row.registration as Record<string, any>)[filterItem.field];

  const cellValue =
    filterItem.field === "languages" && Array.isArray(rawCellValue)
      ? rawCellValue.map((l: { code: string; name: string }) => ({ key: l.code }))
      : rawCellValue;

  const filterValue = filterItem.value?.toString().toLowerCase() ?? "";
  const stringCellValue = String(cellValue ?? "").toLowerCase();
  const numericCellValue = typeof cellValue === "number" ? cellValue : parseFloat(cellValue as string);
  const numericFilterValue = parseFloat(filterValue);

  switch (filterItem.operator) {
    case "includesAnyOf": {
      if (!filterItem.value?.length) return true;
      if (!Array.isArray(cellValue) && typeof cellValue === "object" && cellValue !== null) {
        return filterItem.value.includes((cellValue as { key: string }).key);
      }
      if (!Array.isArray(cellValue)) return true;
      return (cellValue as { key: string }[]).some((item) => filterItem.value.includes(item.key));
    }
    case "=":
      return numericCellValue === numericFilterValue;
    case "!=":
      return numericCellValue !== numericFilterValue;
    case ">":
      return numericCellValue > numericFilterValue;
    case ">=":
      return numericCellValue >= numericFilterValue;
    case "<":
      return numericCellValue < numericFilterValue;
    case "<=":
      return numericCellValue <= numericFilterValue;
    case "contains":
      return stringCellValue.includes(filterValue);
    case "equals":
      return stringCellValue === filterValue;
    case "startsWith":
      return stringCellValue.startsWith(filterValue);
    case "endsWith":
      return stringCellValue.endsWith(filterValue);
    case "isEmpty":
      return !cellValue;
    case "isNotEmpty":
      return !!cellValue;
    default:
      return true;
  }
};

export const buildProvidersCsv = (rows: ProviderRegistrationForDisplay[]): string | null => {
  if (!rows.length) return null;

  const transformed = rows.map(transformRowForCsv);
  const headers = Object.keys(transformed[0]);

  const escape = (v: string) => {
    if (/[",\n\r]/.test(v)) {
      return `"${v.replace(/"/g, '""')}"`;
    }
    return v;
  };

  const lines = [headers.map(escape).join(","), ...transformed.map((row) => headers.map((h) => escape(row[h] ?? "")).join(","))];

  return lines.join("\n");
};
